import type {
  ChallengeDefinition,
  LearningObjective,
  QuestDefinition,
  RewardDefinition,
} from "./schema";

export const foundationObjectives: LearningObjective[] = [
  {
    id: "foundation.explore",
    title: "Explore the sanctuary and find an animal guide",
    sourceSections: [],
  },
];

export const safariObjectives: LearningObjective[] = [
  {
    id: "skeleton.frame",
    title: "Explain that a skeleton is a strong frame of joined bones",
    sourceSections: ["1.1"],
  },
  {
    id: "skeleton.major-bones",
    title: "Identify the skull, jaw, spine, rib cage, hip, arm, and leg bones",
    sourceSections: ["1.1"],
  },
  {
    id: "skeleton.bone-shapes",
    title: "Recognise that bones have different sizes and shapes",
    sourceSections: ["1.1"],
  },
  {
    id: "skeleton.model",
    title: "Compare a skeleton model with a real skeleton",
    sourceSections: ["1.1"],
  },
  {
    id: "skeleton.protect",
    title: "Explain how skeletons protect organs",
    sourceSections: ["1.2"],
  },
  {
    id: "skeleton.shape-support",
    title: "Explain how skeletons give shape and support the body",
    sourceSections: ["1.2"],
  },
  {
    id: "skeleton.movement",
    title: "Explain how skeletons and muscles enable movement",
    sourceSections: ["1.2"],
  },
  {
    id: "skeleton.growth",
    title: "Understand that body growth is supported by skeleton growth",
    sourceSections: ["1.2"],
  },
];

export const foundationRewards: RewardDefinition[] = [
  {
    id: "spark.first-meeting",
    kind: "sanctuary",
    title: "First Knowledge Spark",
  },
];

export const safariRewards: RewardDefinition[] = [
  {
    id: "badge.rabbit-ranger",
    kind: "badge",
    title: "Rabbit Ranger Badge",
  },
  {
    id: "sanctuary.fossil-garden",
    kind: "sanctuary",
    title: "Fossil Garden",
  },
];

export const foundationQuest: QuestDefinition = {
  id: "foundation.meet-guide",
  title: "Meet your chameleon guide",
  objectiveIds: ["foundation.explore"],
  dialogue: [
    {
      id: "guide.welcome",
      speaker: "Chameleon guide",
      text: "You found me! The sanctuary's Knowledge Sparks are hiding. Together, we'll explore animal worlds and bring them home.",
    },
  ],
  rewardIds: ["spark.first-meeting"],
};

export const safariQuest: QuestDefinition = {
  id: "safari.restore-rabbit-trail",
  title: "Restore the Rabbit Trail",
  objectiveIds: safariObjectives.map(({ id }) => id),
  dialogue: [
    {
      id: "rabbit.arrival",
      speaker: "Fern the rabbit",
      text: "The storm scattered our bone clues across the valley. Without them, the young animals cannot learn how their bodies stay strong. Will you help me restore the trail?",
    },
    {
      id: "rabbit.complete",
      speaker: "Fern the rabbit",
      text: "You restored every clue! The skeleton trail is shining again, and every animal can learn from it. You earned the Rabbit Ranger Badge.",
    },
  ],
  rewardIds: safariRewards.map(({ id }) => id),
};

export const safariChallenges: ChallengeDefinition[] = [
  {
    id: "safari.fossil-puzzle",
    stationId: "fossil-puzzle",
    title: "Fossil Puzzle",
    kind: "assembly",
    objectiveIds: [
      "skeleton.frame",
      "skeleton.major-bones",
      "skeleton.bone-shapes",
    ],
    hints: [
      "Start with the skull at the head and the spine down the back of the human model.",
      "The rib cage surrounds the chest, the hip joins the legs, and upper and lower arm and leg bones fit on either side of a joint.",
    ],
    successExplanation:
      "A human skeleton is a strong frame of joined bones. Different shapes fit different jobs: the skull and rib cage form protective spaces, while long bones form the arms and legs.",
  },
  {
    id: "safari.guardian-grove",
    stationId: "guardian-grove",
    title: "Guardian Grove",
    kind: "scenario",
    objectiveIds: ["skeleton.protect"],
    hints: [
      "Look at the human skeleton model and think about which hard bones surround each soft organ.",
      "In a human body, the skull surrounds the brain. The rib cage surrounds the heart and lungs.",
    ],
    successExplanation:
      "In our bodies, the skull protects the brain while the rib cage protects the heart and lungs. Hard bone surrounds these soft, important organs.",
  },
  {
    id: "safari.support-bridge",
    stationId: "support-bridge",
    title: "Support Bridge",
    kind: "scenario",
    objectiveIds: ["skeleton.shape-support", "skeleton.movement"],
    hints: [
      "Imagine trying to stand with no firm frame inside your body.",
      "Your skeleton supports your body and gives it shape. Muscles pull on its bones to create movement.",
    ],
    successExplanation:
      "Our skeleton supports the body, gives it shape, and provides bones for muscles to pull. Bones are hard and cannot move by themselves.",
  },
  {
    id: "safari.model-glade",
    stationId: "model-glade",
    title: "Model Glade",
    kind: "comparison",
    objectiveIds: ["skeleton.model", "skeleton.growth"],
    hints: [
      "The human model can show bone positions and shapes without containing every living part.",
      "A child's skeleton grows as their body grows. An adult skeleton has stopped growing, while a card model never grows and leaves out living tissues.",
    ],
    successExplanation:
      "A human skeleton model helps us see how bones are arranged, but it is simplified and cannot grow or include every living tissue. A child's skeleton grows as the child grows; Fern's rabbit skeleton follows the same big idea.",
  },
  {
    id: "safari.bone-lab",
    stationId: "bone-lab",
    title: "Bone & Body Match",
    kind: "matching",
    objectiveIds: [
      "skeleton.major-bones",
      "skeleton.protect",
      "skeleton.shape-support",
    ],
    hints: [
      "Start with the easiest body clues: the skull is in the head, the rib cage surrounds the chest, and the spine runs down the back.",
      "Match skull—head—protects brain; rib cage—chest—protects heart and lungs; spine—back—supports the body; hip—pelvis—joins legs to the body.",
    ],
    successExplanation:
      "The bone map is complete: the skull protects the brain, the rib cage protects the heart and lungs, the spine supports the back, and the hip joins the legs to the body. Bone shape and position suit each job.",
  },
];

export const muscleObjectives: LearningObjective[] = [
  {
    id: "muscle.pairs",
    title: "Explain that muscles work in pairs",
    sourceSections: ["1.3"],
  },
  {
    id: "muscle.pull-bones",
    title: "Explain that contracting muscles pull on bones to create movement",
    sourceSections: ["1.3"],
  },
  {
    id: "muscle.contract-relax",
    title: "Explain that one muscle contracts while its partner relaxes",
    sourceSections: ["1.3"],
  },
  {
    id: "muscle.shape-change",
    title:
      "Observe that contracting muscles shorten and thicken while relaxing muscles lengthen and thin",
    sourceSections: ["1.3"],
  },
  {
    id: "muscle.model",
    title: "Use a model to show how paired arm muscles work",
    sourceSections: ["1.3"],
  },
  {
    id: "muscle.model-limits",
    title: "Compare a muscle model with a real human arm",
    sourceSections: ["1.3"],
  },
  {
    id: "muscle.movement-health",
    title: "Describe positive ways movement supports health",
    sourceSections: ["1.3"],
  },
];

export const muscleRewards: RewardDefinition[] = [
  { id: "badge.otter-motion", kind: "badge", title: "Beaver Motion Badge" },
  {
    id: "sanctuary.movement-garden",
    kind: "sanctuary",
    title: "Movement Garden",
  },
];

export const muscleQuest: QuestDefinition = {
  id: "muscle.restore-motion-stream",
  title: "Restart the Motion Stream",
  objectiveIds: muscleObjectives.map(({ id }) => id),
  dialogue: [
    {
      id: "otter.arrival",
      speaker: "Mara the beaver",
      text: "The Motion Stream has stopped, and the meadow mechanisms cannot move. Study how human muscles work in pairs, then help me start the stream again!",
    },
    {
      id: "otter.complete",
      speaker: "Mara the beaver",
      text: "The Motion Stream is flowing! You showed how paired muscles pull bones and how movement helps our bodies. You earned the Beaver Motion Badge.",
    },
  ],
  rewardIds: muscleRewards.map(({ id }) => id),
};

export const muscleChallenges: ChallengeDefinition[] = [
  {
    id: "muscle.pair-power",
    stationId: "pair-power",
    title: "Pair Power Workshop",
    kind: "mechanism",
    objectiveIds: [
      "muscle.pairs",
      "muscle.pull-bones",
      "muscle.contract-relax",
      "muscle.shape-change",
    ],
    hints: [
      "To bend an arm, look for the front muscle becoming shorter and thicker while the back muscle becomes longer and thinner.",
      "The front muscle contracts and pulls the lower-arm bone up; its partner at the back relaxes. To straighten the arm, their jobs swap.",
    ],
    successExplanation:
      "Human arm muscles work in pairs. To bend the arm, the front muscle contracts, becoming shorter and thicker as it pulls the bone, while the back muscle relaxes and lengthens. Their jobs swap to straighten the arm.",
  },
  {
    id: "muscle.book-lift",
    stationId: "book-lift",
    title: "Book Lift Lookout",
    kind: "mechanism",
    objectiveIds: [
      "muscle.contract-relax",
      "muscle.shape-change",
      "muscle.pull-bones",
    ],
    hints: [
      "Watch the front of the upper arm as the model lifts the book.",
      "During the lift, the front muscle contracts, shortens, and thickens. It pulls the lower-arm bone upward while the back muscle relaxes.",
    ],
    successExplanation:
      "When the human arm lifts the book, its front upper-arm muscle contracts, shortens, and thickens. It pulls on the lower-arm bone while the partner muscle relaxes, lengthens, and thins.",
  },
  {
    id: "muscle.elastic-model",
    stationId: "elastic-model",
    title: "Elastic Arm Model",
    kind: "comparison",
    objectiveIds: ["muscle.model", "muscle.model-limits"],
    hints: [
      "The card strips represent bones and the elastic bands represent paired muscles.",
      "The model can show paired pulling around a joint, but card and elastic are not living bone, muscle, skin, nerves, or blood vessels.",
    ],
    successExplanation:
      "The card-and-elastic arm is a model: card strips show bones, the fastener shows a joint, and elastic bands show paired pulling. It leaves out living tissues and is not exactly the same as a real arm.",
  },
  {
    id: "muscle.healthy-trail",
    stationId: "healthy-trail",
    title: "Healthy Movement Trail",
    kind: "planning",
    objectiveIds: ["muscle.movement-health"],
    hints: [
      "Choose several enjoyable ways to move rather than one perfect or fastest activity.",
      "Movement can help the heart and lungs work well, strengthen muscles and bones, support sleep and thinking, and lift mood.",
    ],
    successExplanation:
      "Movement can help the heart and lungs work well, strengthen muscles and bones, support sleep and thinking, and lift mood. Many different enjoyable movements count, and rest matters too.",
  },
];

export const coastObjectives: LearningObjective[] = [
  {
    id: "classification.backbone",
    title:
      "Distinguish vertebrates with backbones from invertebrates without backbones",
    sourceSections: ["1.4"],
  },
  {
    id: "classification.exoskeleton",
    title:
      "Explain that an exoskeleton is a hard outer covering that supports and protects some invertebrates",
    sourceSections: ["1.4"],
  },
  {
    id: "classification.five-groups",
    title: "Classify fish, amphibians, reptiles, birds, and mammals",
    sourceSections: ["1.4"],
  },
  {
    id: "classification.observable-traits",
    title:
      "Use disclosed traits such as fins, skin, scales, feathers, and fur to classify animals",
    sourceSections: ["1.4"],
  },
  {
    id: "classification.follow-key",
    title: "Follow a yes-or-no identification key",
    sourceSections: ["1.4"],
  },
  {
    id: "classification.repair-key",
    title: "Repair a branching identification key",
    sourceSections: ["1.4"],
  },
  {
    id: "classification.transfer",
    title: "Classify an unfamiliar animal from disclosed evidence",
    sourceSections: ["1.4"],
  },
];

export const coastRewards: RewardDefinition[] = [
  {
    id: "badge.coast-pathfinder",
    kind: "badge",
    title: "Coast Pathfinder Badge",
  },
  {
    id: "sanctuary.tidepool-observatory",
    kind: "sanctuary",
    title: "Tidepool Observatory",
  },
];

export const coastQuest: QuestDefinition = {
  id: "coast.restore-habitat-beacons",
  title: "Relight the Habitat Beacons",
  objectiveIds: coastObjectives.map(({ id }) => id),
  dialogue: [
    {
      id: "turtle.arrival",
      speaker: "Cove the crab",
      text: "A sea storm muddled our habitat signs, so animals cannot follow the safe paths between land, shore, and water. Observe each visitor's evidence, repair the identification keys, and relight the beacons without disturbing anyone.",
    },
    {
      id: "turtle.complete",
      speaker: "Cove the crab",
      text: "Every Habitat Beacon is shining! Careful observations reunited each visitor with a suitable path. You earned the Coast Pathfinder Badge.",
    },
  ],
  rewardIds: coastRewards.map(({ id }) => id),
};

export const coastChallenges: ChallengeDefinition[] = [
  {
    id: "coast.backbone-beach",
    stationId: "backbone-beach",
    title: "Backbone Beach",
    kind: "scenario",
    objectiveIds: ["classification.backbone"],
    hints: [
      "Read each evidence card: a vertebrate has a backbone inside its body, while an invertebrate has no backbone.",
      "Salmon, puffins, and seals have backbones. Shore crabs, octopuses, and earthworms do not.",
    ],
    successExplanation:
      "Vertebrates are animals with a backbone. Invertebrates are animals without a backbone. Habitat or number of legs does not decide the group; the backbone evidence does.",
  },
  {
    id: "coast.armour-pools",
    stationId: "armour-pools",
    title: "Armour Pools",
    kind: "scenario",
    objectiveIds: ["classification.exoskeleton"],
    hints: [
      "First confirm that every visitor here has no backbone, then look for a hard covering on the outside.",
      "The crab and ladybird have hard jointed outer coverings. The earthworm and octopus have soft bodies without exoskeletons.",
    ],
    successExplanation:
      "An exoskeleton is a hard outer covering that supports and protects some invertebrates, such as crabs and beetles. Other invertebrates, such as earthworms and octopuses, do not have one.",
  },
  {
    id: "coast.five-families",
    stationId: "five-families",
    title: "Five-Group Lookout",
    kind: "scenario",
    objectiveIds: [
      "classification.five-groups",
      "classification.observable-traits",
    ],
    hints: [
      "Use the written evidence: fins and gills suggest fish; smooth moist skin suggests amphibians; dry scales suggest reptiles.",
      "Feathers identify birds in this key, while fur or hair identifies mammals. These are useful school-level traits, not every detail of living diversity.",
    ],
    successExplanation:
      "The salmon is a fish, frog an amphibian, gecko a reptile, puffin a bird, and seal a mammal. This simplified school-level grouping uses disclosed observable traits; scientists may use more evidence for unusual animals.",
  },
  {
    id: "coast.lighthouse-key",
    stationId: "lighthouse-key",
    title: "Lighthouse Identification Key",
    kind: "sequence",
    objectiveIds: ["classification.follow-key"],
    hints: [
      "Begin at the first yes-or-no question for every visitor and follow only the branch matching its evidence card.",
      "Ask about a backbone first. For a vertebrate, continue through labelled trait questions; for an invertebrate, ask about a hard outer covering.",
    ],
    successExplanation:
      "A yes-or-no identification key works by starting at the first question and following one evidence-based branch at a time. Every labelled route ends at a defensible group.",
  },
  {
    id: "coast.storm-key-repair",
    stationId: "storm-key-repair",
    title: "Storm Key Repair",
    kind: "sequence",
    objectiveIds: [
      "classification.repair-key",
      "classification.transfer",
      "classification.observable-traits",
    ],
    hints: [
      "Repair the broadest split first: ask about a backbone before looking at coverings or vertebrate traits.",
      "The unfamiliar newt has a backbone and smooth, moist skin. Follow the repaired key using that disclosed evidence, not its picture.",
    ],
    successExplanation:
      "The repaired key starts with backbone evidence, keeps exoskeleton questions on the invertebrate branch, and uses observable coverings for vertebrates. The unfamiliar newt's backbone and smooth, moist skin identify it as an amphibian.",
  },
];

export const healthObjectives: LearningObjective[] = [
  {
    id: "health.medicine-purpose",
    title:
      "Explain that medicines can treat illness and some can prevent illness",
    sourceSections: ["1.5"],
  },
  {
    id: "health.medicine-safety",
    title:
      "Apply trusted-adult, supplied-instruction, timing, amount, and non-sharing medicine safety",
    sourceSections: ["1.5"],
  },
  {
    id: "health.medicine-forms",
    title: "Recognise varied medicine forms without assuming what each treats",
    sourceSections: ["1.5"],
  },
  {
    id: "health.infectious-disease",
    title:
      "Explain that some germs entering and affecting a living thing can cause infectious disease",
    sourceSections: ["1.5"],
  },
  {
    id: "health.living-things",
    title:
      "Recognise that infectious diseases can affect plants, animals, and people",
    sourceSections: ["1.5"],
  },
  {
    id: "health.vaccination",
    title:
      "Explain that vaccination can prevent some diseases in people and animals",
    sourceSections: ["1.5"],
  },
  {
    id: "health.research-question",
    title:
      "Choose an answerable vaccination question and a trustworthy curated source",
    sourceSections: ["1.5"],
  },
];

export const healthRewards: RewardDefinition[] = [
  {
    id: "badge.habitat-helper",
    kind: "badge",
    title: "Habitat Helper Badge",
  },
  {
    id: "sanctuary.care-greenhouse",
    kind: "sanctuary",
    title: "Care Greenhouse",
  },
];

export const healthQuest: QuestDefinition = {
  id: "health.restore-care-greenhouse",
  title: "Restore the Care Greenhouse",
  objectiveIds: healthObjectives.map(({ id }) => id),
  dialogue: [
    {
      id: "red-panda.arrival",
      speaker: "Sage the panda",
      text: "A gentle glitter-mist muddled our care signs and closed the greenhouse paths. No one is in danger. Will you use trusted guidance and careful science to restore each safe-care lantern?",
    },
    {
      id: "red-panda.complete",
      speaker: "Sage the panda",
      text: "The Care Greenhouse is glowing again! You kept every choice safe, followed trustworthy guidance, and helped plants, animals, and people learn without alarm. You earned the Habitat Helper Badge.",
    },
  ],
  rewardIds: healthRewards.map(({ id }) => id),
};

export const healthChallenges: ChallengeDefinition[] = [
  {
    id: "health.trusted-care-desk",
    stationId: "trusted-care-desk",
    title: "Trusted Care Desk",
    kind: "scenario",
    objectiveIds: ["health.medicine-safety"],
    hints: [
      "A child should never choose, touch, share, or guess about medicine. Look for the responsible-adult or health-professional path.",
      "The complete safe path is: medicine supplied by a responsible adult or health professional, supplied instructions followed for amount and timing, no sharing, and ask the professional when unsure.",
    ],
    successExplanation:
      "Medicine safety always means responsible-adult or health-professional guidance, following the supplied label or instructions for amount and timing, never sharing, and asking that adult or professional rather than guessing.",
  },
  {
    id: "health.forms-and-claims",
    stationId: "forms-and-claims",
    title: "Medicine Forms Gallery",
    kind: "comparison",
    objectiveIds: ["health.medicine-purpose", "health.medicine-forms"],
    hints: [
      "A liquid, tablet, cream, drop, or inhaled form describes how medicine is presented—not which illness it treats.",
      "Medicines can help treat illness and some can help prevent illness, but a responsible adult or health professional follows the supplied instructions and decides what is suitable.",
    ],
    successExplanation:
      "Medicines come in varied forms. The form alone does not show what an illness is or what should be used. Medicines can help treat illness and some help prevent it, always with responsible-adult or health-professional guidance and supplied instructions.",
  },
  {
    id: "health.gentle-germ-trail",
    stationId: "gentle-germ-trail",
    title: "Gentle Germ Trail",
    kind: "sequence",
    objectiveIds: ["health.infectious-disease", "health.living-things"],
    hints: [
      "Start with some germs, then a route into a living thing, then an effect on that plant, animal, or person.",
      "An infectious disease is illness caused when certain germs enter and affect a living thing. Not every germ is harmful, and different diseases spread in different ways.",
    ],
    successExplanation:
      "Some germs can enter and affect living things and cause infectious disease. Plants, animals, and people can be affected, though each disease has its own hosts and routes. Not every germ is harmful.",
  },
  {
    id: "health.prevention-garden",
    stationId: "prevention-garden",
    title: "Prevention Garden",
    kind: "scenario",
    objectiveIds: ["health.living-things", "health.vaccination"],
    hints: [
      "Choose a general action that interrupts the disclosed route: clean garden tools with adult guidance, wash hands after animal care, or cover coughs and wash hands.",
      "General hygiene can interrupt some spread routes. Vaccination can prevent some diseases in people and animals under professional guidance, but not every disease.",
    ],
    successExplanation:
      "Suitable general prevention matches the route: adult-guided tool cleaning for plants, handwashing after animal care, and cough/hand hygiene for people. Vaccination can prevent some diseases in people and animals under professional guidance.",
  },
  {
    id: "health.trustworthy-research",
    stationId: "trustworthy-research",
    title: "Trustworthy Research Greenhouse",
    kind: "comparison",
    objectiveIds: ["health.vaccination", "health.research-question"],
    hints: [
      "Choose a focused question that the two curated organisation cards can answer without sharing anyone's health information.",
      "The World Health Organization card covers prevention in people; the World Organisation for Animal Health card covers prevention and control in animals. Vaccination prevents some, not all, diseases.",
    ],
    successExplanation:
      "A focused question can be checked against curated trustworthy organisations. Their evidence supports that vaccination can prevent some diseases in people and animals; it does not prevent every disease or replace professional guidance.",
  },
];

export const finaleObjectives: LearningObjective[] = [
  {
    id: "review.vocabulary",
    title: "Apply key Living Things vocabulary accurately",
    sourceSections: ["review"],
  },
  {
    id: "review.data-chart",
    title: "Interpret a simple activity bar chart",
    sourceSections: ["review"],
  },
  {
    id: "review.mixed-application",
    title: "Apply ideas from across the chapter in connected rescue contexts",
    sourceSections: ["review"],
  },
  {
    id: "review.earthworm-compost",
    title:
      "Recognise an earthworm as an invertebrate and connect worm compost with plant growth",
    sourceSections: ["review"],
  },
];

export const finaleRewards: RewardDefinition[] = [
  {
    id: "badge.grand-wildlife-rescuer",
    kind: "badge",
    title: "Grand Wildlife Rescuer Badge",
  },
  {
    id: "cosmetic.chameleon-star-scarf",
    kind: "cosmetic",
    title: "Chameleon Star Scarf",
  },
  {
    id: "sanctuary.celebration",
    kind: "sanctuary",
    title: "Sanctuary Celebration",
  },
];

export const finaleQuest: QuestDefinition = {
  id: "finale.grand-wildlife-rescue",
  title: "Complete the Grand Wildlife Rescue",
  objectiveIds: finaleObjectives.map(({ id }) => id),
  dialogue: [
    {
      id: "guide.finale-start",
      speaker: "Chameleon guide",
      text: "Every region is restored, but a gentle night wind has mixed the final sanctuary signals. The animals need your whole Living Things field journal. There is no timer and every clue can be tried again—will you lead the Grand Wildlife Rescue?",
    },
    {
      id: "guide.finale-complete",
      speaker: "Chameleon guide",
      text: "You connected every region and helped the whole sanctuary shine. The celebration can continue whenever you visit, and every trail stays open for exploring and replay.",
    },
  ],
  rewardIds: finaleRewards.map(({ id }) => id),
};

export const finaleChallenges: ChallengeDefinition[] = [
  {
    id: "finale.canopy-frame",
    stationId: "finale-canopy-frame",
    title: "Canopy Frame Rescue",
    kind: "scenario",
    objectiveIds: [
      ...safariObjectives.map(({ id }) => id),
      "review.mixed-application",
    ],
    hints: [
      "Use the field journal's skeleton clues: joined bones support and shape, protective bones surround organs, and muscles pull bones at joints.",
      "Use the bone map: connect each major bone to its body position and job, and remember that a card model leaves out living tissues and growth.",
    ],
    successExplanation:
      "Fern's canopy path is safe: joined supports protect and move at joints, major skeleton positions and jobs are accurate, and the model is not confused with a growing living skeleton.",
  },
  {
    id: "finale.motion-crossing",
    stationId: "finale-motion-crossing",
    title: "Motion Crossing Rescue",
    kind: "mechanism",
    objectiveIds: [
      ...muscleObjectives.map(({ id }) => id),
      "review.data-chart",
      "review.mixed-application",
    ],
    hints: [
      "A contracting muscle pulls, shortens, and thickens while its partner relaxes, lengthens, and thins.",
      "Read a bar chart by comparing labelled bar values. The longest bar answers the observation question without setting a personal target.",
    ],
    successExplanation:
      "Mara the beaver's crossing is open: paired muscles pull around a joint, the activity chart is interpreted from its labelled bars, and varied enjoyable movement is described without body judgment.",
  },
  {
    id: "finale.mystery-habitats",
    stationId: "finale-mystery-habitats",
    title: "Mystery Habitat Rescue",
    kind: "sequence",
    objectiveIds: [
      ...coastObjectives.map(({ id }) => id),
      "review.mixed-application",
    ],
    hints: [
      "Start every key at question 1. Backbone evidence comes before body covering, and habitat alone does not decide an animal group.",
      "A dragonfly has no backbone and a hard jointed exoskeleton; Mara has a backbone and fur, so this school-level key places her with mammals.",
    ],
    successExplanation:
      "Cove's habitat paths are clear: unfamiliar animals are classified from disclosed backbone, exoskeleton, and body-trait evidence through labelled yes-or-no branches.",
  },
  {
    id: "finale.care-signal",
    stationId: "finale-care-signal",
    title: "Safe-Care Signal Rescue",
    kind: "scenario",
    objectiveIds: [
      ...healthObjectives.map(({ id }) => id),
      "review.mixed-application",
    ],
    hints: [
      "Some germs can affect plants, animals, and people, but not every germ is harmful and each disease has particular routes.",
      "Medicine always needs responsible-adult or professional guidance and supplied instructions. Vaccination can prevent some diseases—not every disease.",
    ],
    successExplanation:
      "Sage's signal is safe and accurate: it explains bounded infection routes, complete medicine safeguards, and source-backed vaccination prevention without diagnosis, dosage, or personal advice.",
  },
  {
    id: "finale.worm-garden",
    stationId: "finale-worm-garden",
    title: "Worm Garden Rescue",
    kind: "scenario",
    objectiveIds: [
      "classification.backbone",
      "classification.exoskeleton",
      "review.earthworm-compost",
      "review.mixed-application",
    ],
    hints: [
      "An earthworm has no backbone and no hard outer covering, so follow the soft-bodied invertebrate branch.",
      "Earthworms help break down compost material, and compost returns useful material to soil that supports plant growth.",
    ],
    successExplanation:
      "The friendly earthworm's invertebrate tunnel now reaches the celebration garden, where worm-worked compost supports plant growth without being presented as medicine or a cure.",
  },
  {
    id: "finale.beacon-vocabulary",
    stationId: "finale-beacon-vocabulary",
    title: "Living Things Beacon Rescue",
    kind: "comparison",
    objectiveIds: ["review.vocabulary", "review.mixed-application"],
    hints: [
      "Match each word to evidence: contract/relax describe paired muscle states; vertebrate/invertebrate describe backbone evidence.",
      "Exoskeleton means hard outer support; some germs can cause infectious disease; vaccination can prevent some diseases under trusted guidance.",
    ],
    successExplanation:
      "The final beacon uses precise Living Things vocabulary across skeletons, muscles, classification, infection, vaccination, and medicine safety. The whole sanctuary is ready to celebrate.",
  },
];
